/* 
 *
 * project.js 
 *
 */

import {
    createSlice
} from "@reduxjs/toolkit";
import projectAPI from "../../api/projectAPI";
import {
    fetchTasksByProject,
    deleteTasks,
    createTask,
    splitTask
} from "./task";
import {
    fetchGlossariesByProject,
    assignGlossaryToProject,
    fetchGlossariesByTask,
    unassignGlossaries,
    setGlossaryContractorWrite
} from "./glossary";
import {
    fetchMemoriesByProject,
    updateProjectMemoryPenalties,
    assignMemoryToProject,
    setProjectMemoryWrites,
    unassignMemoryFromProject
} from "./memory";
import {
    fetchProjectAnalyses,
    analyze,
    deleteAnalysis
} from "./analysis";
import thunk from "../../helpers/thunk";

/* thunks */

const uploadProjectFile = thunk(
    "project/uploadProjectFile",
    projectAPI.uploadFile,
    o => ({
        data: o.r.data,
        projectID: o.d.projectID
    })
);

const fetchProject = thunk(
    "project/fetchProject",
    projectAPI.fetchByID,
    o => o.r.data
);

const fetchProjects = thunk(
    "project/fetchProjects",
    projectAPI.fetchAll,
    o => o.r.data
);

const createProject = thunk(
    "project/createProject",
    projectAPI.create,
    o => ({
        project: o.r.data,
        titleID: o.d.titleID
    })
);

const fetchTitleProjects = thunk(
    "project/fetchTitleProjects",
    projectAPI.fetchByTitle,
    o => ({
        projects: o.r.data,
        titleID: o.d.titleID
    })
);

const trashProject = thunk(
    "project/trashProject",
    projectAPI.trash,
    o => o.d.projectID
);

const setProjectName = thunk(
    "project/setProjectName",
    projectAPI.setName,
    o => ({
        ...o.d
    })
);

const setPONumber = thunk(
    "project/setPONumber",
    projectAPI.setPO,
    o => ({
        ...o.d
    })
);

const deletePO = thunk(
    "project/deletePO",
    projectAPI.deletePO,
    o => ({
        projectID: o.d.projectID
    })
);

/* slice */
const slice = createSlice({
    name: "project",
    initialState: {
        projects: {

        }
    },
    reducers: {

    },
    extraReducers: {
        [fetchProject.fulfilled]: (state, action) => {
            const project = action.payload;
            state.projects[project.ID] = {
                name: project.name,
                created: project.created,
                createdBy: project.createdBy,
                titleID: project.titleID,
                orderNumber: project.orderNumber,
                filePath: project.filePath,
                fileName: project.fileName,
                fileType: project.filePath?.split('.').pop(),
                tasks: [],
                memories: {},
                glossaries: {}
            }
        },
        [fetchTitleProjects.fulfilled]: (state, action) => {
            const projects = action.payload.projects;

            projects.forEach(project => {
                state.projects[project.ID] = {
                    name: project.name,
                    created: project.created,
                    createdBy: project.createdBy,
                    titleID: project.titleID,
                    orderNumber: project.orderNumber,
                    filePath: project.filePath,
                    fileType: project.filePath?.split('.').pop(),
                    tasks: [],
                    memories: {},
                    glossaries: {}
                }
            });
        },
        [fetchProjects.fulfilled]: (state, action) => {
            const projects = action.payload;

            projects.forEach(project => {
                state.projects[project.ID] = {
                    name: project.name,
                    created: project.created,
                    createdBy: project.createdBy,
                    titleID: project.titleID,
                    titleName: project.titleName,
                    orderNumber: project.orderNumber,
                    filePath: project.filePath,
                    fileType: project.filePath?.split('.').pop(),
                    tasks: [],
                    memories: {},
                    glossaries: {}
                }
            });
        },
        [fetchTasksByProject.fulfilled]: (state, action) => {
            const tasks = action.payload.tasks;
            const projectID = action.payload.projectID;

            tasks.map(task => {
                state.projects[projectID].tasks.push(task.ID);
            });
        },
        [deleteTasks.fulfilled]: (state, action) => {
            const deletedIDs = action.payload.IDs;
            const projectID = action.payload.projectID;
            deletedIDs.forEach(ID => {
                state.projects[projectID].tasks.splice(state.projects[projectID].tasks.indexOf(ID), 1);
            });
        },
        [createTask.fulfilled]: (state, action) => {
            const tasks = action.payload;
            tasks.forEach(task => {
                const project = state.projects[task.projectID];
                project.tasks.push(task.ID);
            });
        },
        [splitTask.fulfilled]: (state, action) => {
            const tasks = action.payload;

            tasks.forEach(task => {
                state.projects[task.projectID].tasks.push(task.ID);
            });
        },
        [fetchGlossariesByProject.fulfilled]: (state, action) => {
            const glossaries = action.payload.data;
            const projectID = action.payload.projectID;

            glossaries.forEach(glossary => {
                state.projects[projectID].glossaries[glossary.ID] = {
                    contractorWrite: glossary.contractorWrite
                };
            });
        },
        [assignGlossaryToProject.fulfilled]: (state, action) => {
            const glossary = action.payload.data;
            const projectID = action.payload.projectID;

            state.projects[projectID].glossaries[glossary.ID] = {
                contractorWrite: glossary.contractorWrite
            };
        },
        [unassignGlossaries.fulfilled]: (state, action) => {
            const glossaryIDs = action.payload.glossaryIDs;
            const projectID = action.payload.projectID;
            if (projectID) {
                glossaryIDs.forEach(glossaryID => {
                    delete state.projects[projectID].glossaries[glossaryID];
                });
            }
        },
        [createProject.fulfilled]: (state, action) => {
            const project = action.payload.project;

            state.projects[project.ID] = {
                name: project.name,
                created: project.created,
                orderNumber: project.orderNumber,
                createdBy: project.createdBy,
            }
        },
        [uploadProjectFile.fulfilled]: (state, action) => {
            const filePath = action.payload.data.filePath;
            const fileName = action.payload.data.fileName;
            const projectID = action.payload.projectID;

            state.projects[projectID].fileName = fileName;
            state.projects[projectID].filePath = filePath;
            state.projects[projectID].fileType = filePath?.split('.').pop();
        },
        [fetchMemoriesByProject.fulfilled]: (state, action) => {
            const memories = action.payload.data;
            const projectID = action.payload.projectID;

            memories.forEach(memory => state.projects[projectID].memories[memory.ID] = {
                penalty: memory.penalty,
                write: memory.write
            });
        },
        [updateProjectMemoryPenalties.fulfilled]: (state, action) => {
            const memoryIDs = action.payload.memoryIDs;
            const penalty = action.payload.penalty;
            const projectID = action.payload.projectID;

            memoryIDs.forEach(ID => {
                state.projects[projectID].memories[ID].penalty = penalty;
            });
        },
        [assignMemoryToProject.fulfilled]: (state, action) => {
            const memory = action.payload.data;
            const projectID = action.payload.projectID;

            state.projects[projectID].memories[memory.ID] = {
                penalty: memory.penalty,
                write: memory.write
            }
        },
        [setProjectMemoryWrites.fulfilled]: (state, action) => {
            const memoryIDs = action.payload.memoryIDs;
            const write = action.payload.write;
            const projectID = action.payload.projectID;

            memoryIDs.forEach(ID => {
                state.projects[projectID].memories[ID].write = write;
            });
        },
        [fetchProjectAnalyses.fulfilled]: (state, action) => {
            const projectID = action.payload.projectID;
            const analyses = action.payload.data;

            state.projects[projectID].analyses = analyses.map(analysis => analysis.ID);
        },
        [analyze.fulfilled]: (state, action) => {
            const projectID = action.payload.projectID;
            const analysis = action.payload.data;

            state.projects[projectID].analyses.push(analysis.ID);
        },
        [trashProject.fulfilled]: (state, action) => {
            const projectID = action.payload;
            delete state.projects[projectID].filePath;
            delete state.projects[projectID].fileType;
            delete state.projects[projectID].fileName;
            state.projects[projectID].tasks = [];
        },
        [deleteAnalysis.fulfilled]: (state, action) => {
            const analysisIDs = action.payload.analysisIDs;
            const projectID = action.payload.projectID;

            if (projectID == undefined) return;

            analysisIDs.forEach(analysisID => {
                state.projects[projectID].analyses.splice(state.projects[projectID].analyses.indexOf(analysisID), 1);
            });
        },
        [unassignMemoryFromProject.fulfilled]: (state, action) => {
            const projectID = action.payload.projectID;
            const memoryIDs = action.payload.memoryIDs;

            memoryIDs.forEach(memoryID => delete state.projects[projectID].memories[memoryID]);
        },
        [setGlossaryContractorWrite.fulfilled]: (state, action) => {
            const projectID = action.payload.projectID;
            const glossaryIDs = action.payload.glossaryIDs;
            const contractorWrite = action.payload.contractorWrite;

            if (projectID) {
                glossaryIDs.forEach(glossaryID => {
                    state.projects[projectID].glossaries[glossaryID].contractorWrite = contractorWrite;
                });
            }
        },
        [setProjectName.fulfilled] : (state, action) => {
            const projectID = action.payload.projectID;
            const projectName = action.payload.projectName;

            state.projects[projectID].name = projectName;
        },
        [setPONumber.fulfilled] : (state, action) => {
            const projectID = action.payload.projectID;
            const orderNumber = action.payload.orderNumber;

            state.projects[projectID].orderNumber = orderNumber;
        },
        [deletePO.fulfilled] : (state, action) => {
            const projectID = action.payload.projectID;

            delete state.projects[projectID].orderNumber;
            
        }
    }
});

export {
    uploadProjectFile,
    fetchProject,
    fetchProjects,
    createProject,
    fetchTitleProjects,
    trashProject,
    setProjectName,
    setPONumber,
    deletePO
};
export const {} = slice.actions;
export default slice.reducer;